import { formatISO } from 'date-fns'
import { HeightUnit, WeightGoal, WeightPlanType, WeightUnit } from 'libs/weight/types'
import { Answer } from './slice'
import { QuizVersion } from '../../config/types'

export interface Quiz {
  id: string
  quiz_country?: string
  menoscale: Menoscale
  e4: QuizBase
  e5: QuizBase
  e6: QuizBase
  e7: QuizBase
  e8: QuizBase
  e9: QuizBase
  e10: QuizBase
  e11: QuizBase
  e12: QuizBase
  'guest-pass': QuizBase
}

// Common

export type Sex = 'female' | 'male'

export type Gender = 'female' | 'male' | 'pnts' | 'other'

export type AgeRange = '18_34' | '35_49' | '50_64' | '65_plus'

export type HealthGoal = 'live_longer' | 'lose_weight' | 'improve_gut_health' | 'feel_better' | 'sleep_better'

export type GutHealthAwareness = 'just_the_name' | 'a_few_things' | 'everything'

export type DietQuality = 'poor' | 'average' | 'good'

export type EnergyLevel = 'low' | 'inconsistent' | 'high'

export type ProcessedFoodFrequency = 'most_meals' | 'once_a_day' | 'few_times_a_week' | 'rarely_never'

export type DietType =
  | 'no'
  | 'vegetarian'
  | 'vegan'
  | 'mediterranean_style'
  | 'pescatarian'
  | 'gluten_free'
  | 'dairy_free'
  | 'other'

export type SleepHours = 'less_than_5' | '5_6' | '7_8' | '8_plus'

export type PlantFrequency = '1_3' | '4_6' | '7_plus'

export type SleepQuality = 'good' | 'struggle_falling_asleep' | 'wake_up_tired' | 'wake_during_night' | 'hot_flushes'

export type LifestyleActivity = 'not_active' | 'mostly_sedentary' | 'lightly_active' | 'very_active'

export type Motivation = 'getting_healthier' | 'feeling_happier' | 'recent_diagnosis' | 'looking_different' | 'other'

export type RecentReason =
  | 'social_calendar'
  | 'stress_mental_health'
  | 'life_change'
  | 'slower_metabolism'
  | 'pregnancy'
  | 'menopause'
  | 'new_medication'
  | 'injury'
  | 'none'

export type PreviousMethod =
  | 'exercise'
  | 'diet_changes'
  | 'calorie_counting'
  | 'meal_tracking'
  | 'intermittent_fasting'
  | 'glp1s'
  | 'none'

export type MotivationOverTime = 'consistent' | 'fluctuates' | 'unsure'

export type MotivatingEvent =
  | 'holiday'
  | 'wedding'
  | 'summer'
  | 'birthday_party'
  | 'school_reunion'
  | 'other_occasion'
  | 'no'

export type ResultsPace = 'fast' | 'slow' | 'average'

export const weightGoalAnswers = (weightGoal: WeightGoal): Answer[] => [
  {
    question_key: 'goal_type',
    answer: weightGoal.type || 'weight_loss',
  },
  {
    question_key: 'goal_date',
    answer: formatISO(weightGoal.endDate, {
      representation: 'date',
    }),
  },
  ...(weightGoal?.endWeight?.value
    ? [
        {
          question_key: 'goal_weight',
          answer: weightGoal.endWeight.value.toString(),
        } as Answer,
      ]
    : []),
  ...(weightGoal.weightPlan
    ? [
        {
          question_key: 'weight_plan',
          answer: JSON.stringify(
            weightGoal.weightPlan.map(({ date, weight }) => {
              return {
                weight: weight,
                date: formatISO(date, { representation: 'date' }),
              }
            }),
          ),
        } as Answer,
      ]
    : []),
]

export type Bool = 'true' | 'false'

export type Menopause = 'premenopausal' | 'perimenopausal' | 'postmenopausal' | 'do_not_know' | 'prefer_not_to_say'

export type HearAboutUs =
  | 'online'
  | 'influencer'
  | 'zoe_study'
  | 'publication'
  | 'search_engine'
  | 'tv_radio'
  | 'referral'
  | 'zoe_podcast'
  | 'podcast'
  | 'ms_gut_shot_partnership'
  | 'ask_zoe_app'
  | 'daily_30'
  | 'waitrose'
  | 'food_for_life_cookbook'
  | 'other'

export type OnlineAds = 'facebook' | 'instagram' | 'tiktok' | 'youtube' | 'other'

// Quiz Schemas

type MenoscaleSlider = '0' | '1' | '3' | '5'

export type QuizBase = {
  // Metadata
  quiz_version?: QuizVersion
  marketing?: Bool
  user_device_os?: string

  // Biometrics
  height_ft: number
  height_in: number
  height_cm: number
  height_unit: HeightUnit
  weight_lbs?: number
  weight_kg?: number
  ideal_weight_lbs?: number
  ideal_weight_kg?: number
  weight_unit: WeightUnit
  sex: Sex
  gender: Gender
  gender_other?: string | null
  age_range: AgeRange

  // E3: Entry & goals
  welcome_age_range?: AgeRange
  goals?: HealthGoal[]
  heard_about_gut_health?: GutHealthAwareness

  // E3: Lifestyle
  diet_quality?: DietQuality
  snacker?: Bool
  energy?: EnergyLevel
  processed_food_frequency?: ProcessedFoodFrequency
  diet_type?: DietType | DietType[]
  sleep_hours?: SleepHours
  plant_frequency?: PlantFrequency
  sleep_quality?: SleepQuality[]
  lifestyle_activity?: LifestyleActivity
  biological_sex?: Sex
  antibiotics?: Bool
  blood_tests?: Bool
  glp1s?: Bool

  // E3: Weight loss branch
  motivation?: Motivation
  recent_reasons?: RecentReason[]
  previous_methods?: PreviousMethod[]
  eat_distracted?: Bool
  healthy_but_no_results?: Bool
  returning_to_habits?: Bool
  motivation_over_time?: MotivationOverTime
  motivating_event?: MotivatingEvent
  results_pace?: ResultsPace

  // E3: Gut quiz
  gut_dna_question?: Bool
  gut_impacts_health_question?: Bool
  gut_change_in_days_question?: Bool

  // Health & lifestyle (shared)
  health_goal?: HealthGoal
  health_goal_other?: string | null
  has_weight_goal?: Bool
  know_your_weight?: string
  hunger_level?: string
  taken_antibiotics?: Bool
  sleep?: string
  pregnant?: Bool
  plants_in_diet?: string
  gut_health?: string
  menopause?: Menopause
  health_condition?: string | string[]
  life_event?: string[]
  location?: Bool

  // Medical
  diabetes_or_prediabetes?: Bool
  diabetes_in_family?: Bool
  cardiovascular_disease_diagnosed_or_high_cholesterol?: Bool
  cardiovascular_disease_in_family?: Bool
  blood_pressure_diagnosed?: Bool
  blood_pressure_medication?: Bool

  // Weight goals
  goal_weight?: number
  goal_date?: string
  goal_type?: WeightPlanType
  weight_plan?: string
  target_event_date_weight_loss?: string
  has_flexible_timeline?: string

  // e5: Symptom questionnaire (slider answers stored as '1'-'4', where 1 = Extremely / worst)
  digestive_symptoms?: string
  irregular_bowel_movements?: string
  hunger?: string
  cravings?: string
  joint_and_muscular_discomfort?: string
  decreased_physical_strength_or_stamina?: string
  declining_skin_quality?: string
  declining_hair_quality?: string
  irritability?: string
  low_mood_or_mood_swings?: string
  feeling_stressed_or_on_edge?: string
  sleep_problems?: string
  tiredness_and_fatigue?: string
  brain_fog?: string

  // e7 ("Symptom score v2"): symptom questionnaire (slider answers stored '1'-'4',
  // where 1 = Not at all / best and 4 = Extremely / worst — the reverse of e5).
  // Keys reused from above: irregular_bowel_movements, hunger, cravings,
  // joint_and_muscular_discomfort, decreased_physical_strength_or_stamina,
  // tiredness_and_fatigue, irritability.
  digestive_discomfort?: string
  bloating?: string
  acid_reflux_and_indigestion?: string
  skin_issues?: string
  hair_loss?: string
  brain_fog_or_trouble_concentrating?: string
  poor_sleep_quality?: string
  stress_or_low_mood?: string
  weight_gain_or_difficulty_managing_weight?: string
  // Hormonal (conditional). hot_flushes / night_sweats / loss_of_interest_in_sex
  // are also declared on the Menoscale extension as MenoscaleSlider; the
  // intersection narrows cleanly since MenoscaleSlider is a string subtype.
  night_sweats?: string
  hot_flushes?: string
  loss_of_interest_in_sex?: string

  // e5: derived symptom score (computed on the results screen so analytics
  // can track the bucket the user landed in)
  symptom_score?: string
  symptom_score_category?: string
  symptom_score_main_symptoms?: string[]
  // e7: top-3 key symptoms surfaced on the results screen
  symptom_score_key_symptoms?: string[]

  // Demographics
  kids_under_18?: string
  kids_under_5?: Bool
  length_of_time?: string
  hear_about_us?: HearAboutUs
  hear_about_us_influencer?: string
  hear_about_us_podcast?: string
  hear_about_us_publication?: string
  hear_about_us_online?: OnlineAds
  hear_about_us_other?: string
}

export type MenoscaleConditions = Partial<Menoscale> & {
  quiz_country?: string
}

export type QuizConditions = Partial<QuizBase> & {
  has_target_date_weight_loss?: Bool
  is_frequently_hungry?: Bool
  has_weight_loss_goal?: Bool
  no_weight_loss_goal?: Bool
  quiz_country?: string
  is_country_supported?: Bool
  available_in_location?: Bool
  has_blood_sugar_or_blood_fast_risk?: Bool
  weight_plan?: string
}

export type FullConditions = {
  [key: string]: string | string[] | undefined
}

export type Conditions = QuizConditions | MenoscaleConditions | FullConditions

export type Menoscale = QuizBase & {
  age: string
  menopause?: Menopause
  menopause_hormonal_treatment?: 'currently_taking' | 'taken_in_past' | 'never_taken'
  hot_flushes?: MenoscaleSlider
  night_sweats?: MenoscaleSlider
  palpitations?: MenoscaleSlider
  joint_and_muscular_discomfort?: MenoscaleSlider
  weight_gain?: MenoscaleSlider
  loss_of_interest_in_sex?: MenoscaleSlider
  bladder_problems?: MenoscaleSlider
  vaginal_dryness?: MenoscaleSlider
  declining_skin_quality?: MenoscaleSlider
  declining_hair_quality?: MenoscaleSlider
  irritability?: MenoscaleSlider
  low_mood_depression_mood_swings?: MenoscaleSlider
  anxiety?: MenoscaleSlider
  sleep_problems?: MenoscaleSlider
  headaches?: MenoscaleSlider
  brain_fog?: MenoscaleSlider
  memory_problems?: MenoscaleSlider
  tiredness_and_fatigue?: MenoscaleSlider
  decreased_physical_strength?: MenoscaleSlider
  digestive_symptoms?: MenoscaleSlider
  menoscale_science_consent?: Bool
  menoscale_score?: string
  menoscale_main_symptoms?: string
}

export type Quizzes = QuizBase | Menoscale

// Shared shape for components that read answers across configurations
// (e.g. MultipleSlidersScreen). Every slider question key across our quiz
// configs is a key of QuizBase or the menoscale extension, so the union of
// possible answer keys is `keyof Menoscale` and a "bag of answers" is a
// partial view of that.
export type QuizAnswerKey = keyof Menoscale
export type QuizAnswerBag = Partial<Menoscale>
